import React, { useMemo, useContext } from "react";
import { useSyncedField, SidebarContext } from "../../components/Sidebar";
import { isBitSet, resolveEntityKindBitLocations } from "../../utils";
import StarekContext from "../../context/StarekContext";

const TYPE = {
    PERMISSION: 1,
    ASSOCIATIONS: 2,
    ACTION: 4,
};

function entityLabel(et) {
    return et.plural || et.code;
}

const KEYS_BY_TYPE = {
    [TYPE.PERMISSION]: ["read", "modify", "delete"],
    [TYPE.ASSOCIATIONS]: ["link", "unlink"],
    [TYPE.ACTION]: ["execute"],
};

const shapeForType = (t) =>
    Object.fromEntries((KEYS_BY_TYPE[t] || []).map((k) => [k, false]));

function ToggleSwitch({ id, checked, onChange }) {
    return (
        <div className="form-check form-switch mb-0 d-flex justify-content-center">
            <input
                id={id}
                type="checkbox"
                className="form-check-input"
                role="switch"
                checked={checked}
                onChange={onChange}
                style={{
                    backgroundColor: checked ? "#0d6efd" : "#6c757d",
                    borderColor: checked ? "#0d6efd" : "#6c757d",
                    width: "2.5rem",
                    height: "1.25rem",
                    cursor: "pointer",
                }}
            />
        </div>
    );
}

function SelectAllButton({ allOn, onClick }) {
    return (
        <button
            type="button"
            className={`btn btn-sm rounded-circle d-inline-flex align-items-center justify-content-center ${allOn ? "btn-primary" : "btn-dark"}`}
            title={allOn ? "Clear all" : "Select all"}
            onClick={onClick}
            style={{
                width: "26px",
                height: "26px",
                padding: 0,
                transition: "transform 0.15s ease, box-shadow 0.15s ease",
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.2)";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
            }}
        >
            <i className="fas fa-check-double" style={{ fontSize: "0.6rem" }} />
        </button>
    );
}

const TableStyles = () => (
    <style>{`
      .perm-table { width: 100%; table-layout: fixed; border-collapse: separate; border-spacing: 0; }
      .perm-table col.resource { width: auto; }
      .perm-table col.perm { width: 95px; }
      .perm-table col.row-action { width: 36px; }
      .perm-table thead th { padding: 8px; font-weight: 600; color: #6c757d; font-size: 0.85rem; }
      .perm-table tbody td { padding: 8px; border-top: 1px solid #eee; vertical-align: middle; }
      .perm-table th.perm, .perm-table td.perm { text-align: center; }
      .perm-table td.row-action { text-align: center; }
    `}</style>
);

const SectionTitle = ({ children }) => (
    <div className="d-flex align-items-center mt-3 mb-1">
        <h6 className="mb-0 text-uppercase text-muted">{children}</h6>
        <hr className="flex-grow-1 ms-2 my-1" />
    </div>
);

const PermissionGroupTable = ({ rows, permissions, togglePerm, toggleAllForRow }) =>
    !rows?.length ? null : (
        <div className="mb-3">
            <TableStyles />
            <SectionTitle>Permissions</SectionTitle>
            <table className="perm-table">
                <colgroup>
                    <col className="resource" />
                    <col className="perm" />
                    <col className="perm" />
                    <col className="perm" />
                    <col className="row-action" />
                </colgroup>
                <thead>
                    <tr>
                        <th>Resource</th>
                        <th className="perm">Delete</th>
                        <th className="perm">Add / Modify</th>
                        <th className="perm">Read</th>
                        <th />
                    </tr>
                </thead>
                <tbody>
                    {rows.map((et) => {
                        const perms = permissions[et.code] || {};
                        const keys = ["delete", "modify", "read"];
                        const allOn = keys.every((k) => perms[k]);
                        return (
                            <tr key={et.id}>
                                <td title={et.description}>{entityLabel(et)}</td>
                                <td className="perm">
                                    <ToggleSwitch
                                        id={`${et.code}-delete`}
                                        checked={Boolean(perms.delete)}
                                        onChange={() => togglePerm(et.code, "delete")}
                                    />
                                </td>
                                <td className="perm">
                                    <ToggleSwitch
                                        id={`${et.code}-modify`}
                                        checked={Boolean(perms.modify)}
                                        onChange={() => togglePerm(et.code, "modify")}
                                    />
                                </td>
                                <td className="perm">
                                    <ToggleSwitch
                                        id={`${et.code}-read`}
                                        checked={Boolean(perms.read)}
                                        onChange={() => togglePerm(et.code, "read")}
                                    />
                                </td>
                                <td className="row-action">
                                    <SelectAllButton
                                        allOn={allOn}
                                        onClick={() => toggleAllForRow(et.code, keys)}
                                    />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );

const RelationGroupTable = ({ rows, permissions, togglePerm, toggleAllForRow }) =>
    !rows?.length ? null : (
        <div className="mb-3">
            <TableStyles />
            <SectionTitle>Associations</SectionTitle>
            <table className="perm-table">
                <colgroup>
                    <col className="resource" />
                    <col className="perm" />
                    <col className="perm" />
                    <col className="row-action" />
                </colgroup>
                <thead>
                    <tr>
                        <th>Resource</th>
                        <th className="perm">Unlink</th>
                        <th className="perm">Link</th>
                        <th />
                    </tr>
                </thead>
                <tbody>
                    {rows.map((et) => {
                        const perms = permissions[et.code] || {};
                        const keys = ["unlink", "link"];
                        const allOn = keys.every((k) => perms[k]);
                        return (
                            <tr key={et.id}>
                                <td title={et.description}>{entityLabel(et)}</td>
                                <td className="perm">
                                    <ToggleSwitch
                                        id={`${et.code}-unlink`}
                                        checked={Boolean(perms.unlink)}
                                        onChange={() => togglePerm(et.code, "unlink")}
                                    />
                                </td>
                                <td className="perm">
                                    <ToggleSwitch
                                        id={`${et.code}-link`}
                                        checked={Boolean(perms.link)}
                                        onChange={() => togglePerm(et.code, "link")}
                                    />
                                </td>
                                <td className="row-action">
                                    <SelectAllButton
                                        allOn={allOn}
                                        onClick={() => toggleAllForRow(et.code, keys)}
                                    />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );

const ActionGroupTable = ({ rows, permissions, togglePerm }) =>
    !rows?.length ? null : (
        <div className="mb-3">
            <TableStyles />
            <SectionTitle>Actions</SectionTitle>
            <table className="perm-table">
                <colgroup>
                    <col className="resource" />
                    <col className="perm" />
                </colgroup>
                <thead>
                    <tr>
                        <th>Resource</th>
                        <th className="perm">Execute</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((et) => {
                        const perms = permissions[et.code] || {};
                        return (
                            <tr key={et.id}>
                                <td title={et.description}>{entityLabel(et)}</td>
                                <td className="perm">
                                    <ToggleSwitch
                                        id={`${et.code}-execute`}
                                        checked={Boolean(perms.execute)}
                                        onChange={() => togglePerm(et.code, "execute")}
                                    />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );

const RolePermissions = () => {
    const { entity } = useContext(SidebarContext);
    const starekContext = useContext(StarekContext);
    const [permissions, setPermissions] = useSyncedField("permissions", {});

    const entityTypes = useMemo(() => {
        return entity?.entityTypes || [];
    }, [entity]);

    const typeBitLocation = useMemo(() => {
        const resolved = resolveEntityKindBitLocations(starekContext?.types);
        return {
            [TYPE.PERMISSION]: resolved.entity,
            [TYPE.ASSOCIATIONS]: resolved.association,
            [TYPE.ACTION]: resolved.action,
        };
    }, [starekContext?.types]);

    const groups = useMemo(() => {
        const g = {
            [TYPE.PERMISSION]: [],
            [TYPE.ASSOCIATIONS]: [],
            [TYPE.ACTION]: [],
        };
        entityTypes.forEach((et) => {
            if (isBitSet(et?.type, typeBitLocation[TYPE.PERMISSION])) {
                g[TYPE.PERMISSION].push(et);
            }
            if (isBitSet(et?.type, typeBitLocation[TYPE.ASSOCIATIONS])) {
                g[TYPE.ASSOCIATIONS].push(et);
            }
            if (isBitSet(et?.type, typeBitLocation[TYPE.ACTION])) {
                g[TYPE.ACTION].push(et);
            }
        });
        Object.values(g).forEach((arr) =>
            arr.sort((a, b) => entityLabel(a).localeCompare(entityLabel(b))),
        );
        return g;
    }, [entityTypes, typeBitLocation]);

    const togglePerm = (entityCode, permKey) => {
        const current = permissions[entityCode] || {};
        const newVal = !current[permKey];
        const updated = { ...current, [permKey]: newVal };

        // Selecting a higher-level permission auto-enables its prerequisites
        if (newVal) {
            if (permKey === "delete") {
                updated.modify = true;
                updated.read = true;
            } else if (permKey === "modify") {
                updated.read = true;
            }
        }

        setPermissions({ ...permissions, [entityCode]: updated });
    };

    const toggleAllForRow = (entityCode, keys) => {
        const current = permissions[entityCode] || {};
        const allOn = keys.every((k) => current[k]);
        const updated = Object.fromEntries(keys.map((k) => [k, !allOn]));
        setPermissions({ ...permissions, [entityCode]: { ...current, ...updated } });
    };

    return (
        <div className="p-3">
            <PermissionGroupTable
                rows={groups[TYPE.PERMISSION]}
                permissions={permissions}
                togglePerm={togglePerm}
                toggleAllForRow={toggleAllForRow}
            />
            <RelationGroupTable
                rows={groups[TYPE.ASSOCIATIONS]}
                permissions={permissions}
                togglePerm={togglePerm}
                toggleAllForRow={toggleAllForRow}
            />
            <ActionGroupTable
                rows={groups[TYPE.ACTION]}
                permissions={permissions}
                togglePerm={togglePerm}
            />
        </div>
    );
};

export default RolePermissions;
